import { CustomerCard } from '@/components/CustomerCard';
import Debriefing from '@/components/Debriefing';
import { DebugPanel } from '@/components/DebugPanel';
import { DecisionPanel } from '@/components/DecisionPanel';
import { EuropeMap } from '@/components/EuropeMap';
import { ResultModal } from '@/components/ResultModal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { loadScenario } from '@/data/scenarioLoader';
import {
  calculateFinalScore,
  generateRecommendations,
  generateSkillGaps,
  generateTimelineEvents,
} from '@/utils/debriefingLogic';
import {
  calculateCommunicationTiming,
  calculateCSAT,
  calculateTotalCost,
  formatCurrency,
  formatGameTime,
  formatGameTimeWithDay,
  getCommunicationCSAT
} from '@/utils/gameLogic';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  Clock,
  FastForward,
  Pause,
  Play,
  Settings,
  Users
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export const GameScreen = ({ scenario, onExit }) => {
  const [gameTime, setGameTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [gameSpeed, setGameSpeed] = useState(1);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedDecisions, setSelectedDecisions] = useState({});
  const [handledCustomers, setHandledCustomers] = useState([]);
  const [currentResult, setCurrentResult] = useState(null);
  const [loadedScenarioData, setLoadedScenarioData] = useState(null);
  const [budget, setBudget] = useState(scenario.budget || 0);
  const [showDebug, setShowDebug] = useState(false);
  const [showDebriefing, setShowDebriefing] = useState(false);
  const [debriefingData, setDebriefingData] = useState(null);
  const [isTimeSkipping, setIsTimeSkipping] = useState(false);
  const [showTimeSkipModal, setShowTimeSkipModal] = useState(false);
  const [timeSkipInfo, setTimeSkipInfo] = useState(null);
  const lastCustomerCountRef = useRef(0);
  const timeSkipNotificationShownRef = useRef(false);
  const isProcessingRef = useRef(false);
  const [stats, setStats] = useState({
    totalCsat: 0,
    customersHandled: 0,
    budgetSpent: 0,
    avgResponseTime: 0
  });

  // Track when each customer was selected (for communication timing calculation)
  const [customerTracking, setCustomerTracking] = useState({});

  // Track customer results for debriefing
  const [customerResults, setCustomerResults] = useState([]);

  // Active routes for map visualization
  const [activeRoutes, setActiveRoutes] = useState([]);

  // Initialize customers and load scenario data
  useEffect(() => {
    const loadedScenario = loadScenario(scenario.id);
    if (loadedScenario) {
      setLoadedScenarioData(loadedScenario);

      // Reset game state for new scenario
      setCustomerResults([]);
      setHandledCustomers([]);
      setGameTime(0);
      setStats({
        totalCsat: 0,
        customersHandled: 0,
        budgetSpent: 0,
        avgResponseTime: 0
      });
      isProcessingRef.current = false;

      // Initialize customers that should appear at scenario start time
      if (loadedScenario.customers) {
        const scenarioStartTime = loadedScenario.time?.start || '08:00';
        const [startHours, startMinutes] = scenarioStartTime.split(':').map(Number);
        const startTimeMinutes = (startHours || 8) * 60 + (startMinutes || 0);

        // Show customers that appear at or before start time (day 1, at or before start time)
        const initialCustomers = loadedScenario.customers.filter(customer => {
          const [appearHours, appearMinutes] = customer.appears_at.split(':').map(Number);
          const customerDay = customer.appears_at_day || 1;
          // Only show customers from day 1 that appear at or before scenario start time
          if (customerDay !== 1) return false;
          const appearTimeMinutes = appearHours * 60 + appearMinutes;
          return appearTimeMinutes <= startTimeMinutes;
        });

        setCustomers(initialCustomers);
        lastCustomerCountRef.current = initialCustomers.length;
      }

      // Reset time skip notification flag for new scenario
      timeSkipNotificationShownRef.current = false;

      // Check for initial time event and show modal if exists
      const scenarioStartTime = loadedScenario.time?.start || '08:00';
      const [startHours, startMinutes] = scenarioStartTime.split(':').map(Number);
      const timeEvents = loadedScenario.time_events || [];
      const initialEvent = timeEvents.find(event => {
        const [eventHours, eventMinutes] = event.time.split(':').map(Number);
        return event.day === 1 && eventHours === startHours && eventMinutes === startMinutes;
      });

      if (initialEvent) {
        // Show initial event modal after a short delay to allow UI to render
        setTimeout(() => {
          const initialCustomersCount = loadedScenario.customers?.filter(customer => {
            const [appearHours, appearMinutes] = customer.appears_at.split(':').map(Number);
            const customerDay = customer.appears_at_day || 1;
            if (customerDay !== 1) return false;
            const appearTimeMinutes = appearHours * 60 + appearMinutes;
            const startTimeMinutes = (startHours || 8) * 60 + (startMinutes || 0);
            return appearTimeMinutes <= startTimeMinutes;
          }).length || 0;

          // Calculate start time in seconds for formatting
          const startTimeSeconds = (startHours || 8) * 3600 + (startMinutes || 0) * 60;

          setTimeSkipInfo({
            timeText: '0 minutes',
            currentTime: formatGameTimeWithDay(startTimeSeconds),
            minutesToSkip: 0,
            timeScale: loadedScenario.time?.time_scale || 12,
            newCustomersCount: initialCustomersCount,
            timeSkipped: false, // Not skipped, just initial state
            eventDescription: initialEvent.description
          });
          setShowTimeSkipModal(true);
        }, 500);
      }

      // Use routes from scenario if available, otherwise generate from customers
      if (loadedScenario.routes && loadedScenario.routes.length > 0) {
        setActiveRoutes(loadedScenario.routes);
      } else if (loadedScenario.customers) {
        // Fallback: generate routes from customers
        const routes = loadedScenario.customers.map(customer => ({
          from: customer.trip.from,
          to: customer.trip.to,
          status: 'normal'
        }));
        setActiveRoutes(routes);
      }

      // Update budget if available in loaded scenario
      if (loadedScenario.budget) {
        setBudget(loadedScenario.budget);
      }
    } else {
      console.error(`Failed to load scenario: ${scenario.id}`);
    }
  }, [scenario.id]);

  // Use loaded scenario data or fallback to original scenario
  const currentScenario = loadedScenarioData || scenario;

  // Game timer
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setGameTime(prev => prev + gameSpeed);
    }, 1000);

    return () => clearInterval(interval);
  }, [isPaused, gameSpeed]);

  // Dynamic customer appearance based on appears_at time
  useEffect(() => {
    if (!loadedScenarioData || !currentScenario.time) return;

    const getScenarioStartTimeInSeconds = () => {
      const startTime = currentScenario.time?.start || '08:00';
      const [hours, minutes] = startTime.split(':').map(Number);
      return (hours || 8) * 3600 + (minutes || 0) * 60;
    };

    const scenarioStartSeconds = getScenarioStartTimeInSeconds();
    const timeScale = currentScenario.time?.time_scale || 12;
    const elapsedSeconds = gameTime * timeScale;
    const currentGameTimeSeconds = scenarioStartSeconds + elapsedSeconds;

    // Ensure currentGameTimeSeconds is valid
    const validGameTimeSeconds = isNaN(currentGameTimeSeconds) || currentGameTimeSeconds < 0
      ? scenarioStartSeconds
      : currentGameTimeSeconds;

    // Convert to minutes for comparison
    const currentTimeMinutes = Math.floor(validGameTimeSeconds / 60);

    // Check which customers should appear now (excluding already handled ones)
    const allCustomers = loadedScenarioData.customers || [];
    const handledCustomerIds = new Set(handledCustomers.map(c => c.id));

    const visibleCustomers = allCustomers.filter(customer => {
      // Don't show already handled customers
      if (handledCustomerIds.has(customer.id)) {
        return false;
      }

      // Check if customer should appear at current time
      // appears_at format: "HH:MM" and appears_at_day for multi-day scenarios (days start from 1)
      const [appearHours, appearMinutes] = customer.appears_at.split(':').map(Number);
      const customerDay = customer.appears_at_day || 1;
      const appearTimeMinutes = (customerDay - 1) * 24 * 60 + appearHours * 60 + appearMinutes;
      return appearTimeMinutes <= currentTimeMinutes;
    });

    // Update customers list
    setCustomers(visibleCustomers);

    lastCustomerCountRef.current = visibleCustomers.length;
  }, [gameTime, loadedScenarioData, currentScenario, handledCustomers]);

  // Separate effect to update time skip modal info when customers appear after skip
  useEffect(() => {
    if (!showTimeSkipModal || !timeSkipInfo?.timeSkipped || !loadedScenarioData || !currentScenario.time) {
      return;
    }

    const newCustomersCount = customers.length;
    const getScenarioStartTimeInSeconds = () => {
      const startTime = currentScenario.time?.start || '08:00';
      const [hours, minutes] = startTime.split(':').map(Number);
      return hours * 3600 + minutes * 60;
    };
    const scenarioStartSeconds = getScenarioStartTimeInSeconds();
    const timeScale = currentScenario.time?.time_scale || 12;
    const elapsedSeconds = gameTime * timeScale;
    const currentGameTimeSeconds = scenarioStartSeconds + elapsedSeconds;
    const currentTimeStr = formatGameTimeWithDay(currentGameTimeSeconds);

    // Only update if values actually changed (use functional update to avoid dependency on timeSkipInfo)
    setTimeSkipInfo(prev => {
      if (!prev) return prev;

      // Only update if values actually changed
      if (prev.newCustomersCount === newCustomersCount && prev.currentTime === currentTimeStr) {
        return prev;
      }

      return {
        ...prev,
        newCustomersCount,
        currentTime: currentTimeStr,
        eventDescription: prev.eventDescription // Preserve event description
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers.length, gameTime, showTimeSkipModal, loadedScenarioData, currentScenario]);

  // Helper function to check and skip time if needed
  const checkAndSkipTime = () => {
    // Don't skip if notification already shown or if conditions not met
    if (!loadedScenarioData || !currentScenario.time || isPaused || customers.length > 0 || handledCustomers.length === 0) {
      return;
    }

    const getScenarioStartTimeInSeconds = () => {
      const startTime = currentScenario.time?.start || '08:00';
      const [hours, minutes] = startTime.split(':').map(Number);
      return hours * 3600 + minutes * 60;
    };

    const scenarioStartSeconds = getScenarioStartTimeInSeconds();
    const timeScale = currentScenario.time?.time_scale || 12;
    const elapsedSeconds = gameTime * timeScale;
    const currentGameTimeSeconds = scenarioStartSeconds + elapsedSeconds;
    const currentTimeMinutes = Math.floor(currentGameTimeSeconds / 60);

    // Find next customer appearance time
    const allCustomers = loadedScenarioData.customers || [];
    const handledCustomerIds = new Set(handledCustomers.map(c => c.id));

    const futureCustomers = allCustomers.filter(customer => {
      if (handledCustomerIds.has(customer.id)) return false;
      const [appearHours, appearMinutes] = customer.appears_at.split(':').map(Number);
      const customerDay = customer.appears_at_day || 1;
      const appearTimeMinutes = (customerDay - 1) * 24 * 60 + appearHours * 60 + appearMinutes;
      return appearTimeMinutes > currentTimeMinutes;
    });

    if (futureCustomers.length > 0) {
      // Find the earliest future customer
      const nextCustomer = futureCustomers.reduce((earliest, customer) => {
        const [earliestHours, earliestMinutes] = earliest.appears_at.split(':').map(Number);
        const [customerHours, customerMinutes] = customer.appears_at.split(':').map(Number);
        const earliestDay = earliest.appears_at_day || 1;
        const customerDay = customer.appears_at_day || 1;
        const earliestMinutesTotal = (earliestDay - 1) * 24 * 60 + earliestHours * 60 + earliestMinutes;
        const customerMinutesTotal = (customerDay - 1) * 24 * 60 + customerHours * 60 + customerMinutes;
        return customerMinutesTotal < earliestMinutesTotal ? customer : earliest;
      });

      const [nextHours, nextMinutes] = nextCustomer.appears_at.split(':').map(Number);
      const nextDay = nextCustomer.appears_at_day || 1;
      const nextTimeMinutes = (nextDay - 1) * 24 * 60 + nextHours * 60 + nextMinutes;
      const minutesToSkip = nextTimeMinutes - currentTimeMinutes;

      if (minutesToSkip > 0) {
        // Automatically skip time
        const secondsToSkip = minutesToSkip * 60;
        const gameTimeToAdd = Math.ceil(secondsToSkip / timeScale);

        const hoursToSkip = Math.floor(minutesToSkip / 60);
        const minsToSkip = minutesToSkip % 60;
        let timeText = '';
        if (hoursToSkip > 0) {
          timeText = `${hoursToSkip} hour${hoursToSkip > 1 ? 's' : ''}`;
          if (minsToSkip > 0) {
            timeText += ` and ${minsToSkip} minute${minsToSkip > 1 ? 's' : ''}`;
          }
        } else {
          timeText = `${minsToSkip} minute${minsToSkip > 1 ? 's' : ''}`;
        }

        // Skip time using functional update
        setGameTime(prev => {
          const newGameTime = prev + gameTimeToAdd;
          const newElapsedSeconds = newGameTime * timeScale;
          const newCurrentGameTimeSeconds = scenarioStartSeconds + newElapsedSeconds;
          const newCurrentTimeMinutes = Math.floor(newCurrentGameTimeSeconds / 60);

          // Count new customers that will appear
          const newVisibleCustomers = allCustomers.filter(customer => {
            if (handledCustomerIds.has(customer.id)) return false;
            const [appearHours, appearMinutes] = customer.appears_at.split(':').map(Number);
            const customerDay = customer.appears_at_day || 1;
            const appearTimeMinutes = (customerDay - 1) * 24 * 60 + appearHours * 60 + appearMinutes;
            return appearTimeMinutes <= newCurrentTimeMinutes;
          });

          const newCustomersCount = newVisibleCustomers.length;

          // Calculate new day and time for event description lookup
          const newDay = Math.floor(newCurrentTimeMinutes / (24 * 60)) + 1;
          const newTimeInDay = newCurrentTimeMinutes % (24 * 60);
          const newHours = Math.floor(newTimeInDay / 60);
          const newMinutes = newTimeInDay % 60;
          const newTimeString = `${String(newHours).padStart(2, '0')}:${String(newMinutes).padStart(2, '0')}`;

          // Find matching time event description
          const timeEvents = currentScenario.time_events || [];
          const matchingEvent = timeEvents.find(event => {
            const [eventHours, eventMinutes] = event.time.split(':').map(Number);
            const eventTimeMinutes = eventHours * 60 + eventMinutes;
            // Match if same day and time is within 30 minutes (to handle slight timing differences)
            return event.day === newDay && Math.abs(eventTimeMinutes - newTimeInDay) <= 30;
          });

          // Show modal after a short delay to allow state updates
          setTimeout(() => {
            setTimeSkipInfo({
              timeText,
              currentTime: formatGameTimeWithDay(newCurrentGameTimeSeconds),
              minutesToSkip: 0, // Already skipped
              timeScale,
              newCustomersCount,
              timeSkipped: true,
              eventDescription: matchingEvent?.description || null
            });
            setShowTimeSkipModal(true);
            setIsTimeSkipping(true);
            timeSkipNotificationShownRef.current = true;
          }, 300);

          return newGameTime;
        });
      }
    }
  };

  // Auto-trigger time skip when all customers are handled
  useEffect(() => {
    // Only trigger if:
    // - No customers waiting
    // - At least one customer has been handled
    // - Result modal is not shown
    // - Not already showing time skip modal
    // - Not paused
    // - Scenario loaded
    // - Notification not already shown
    if (customers.length === 0 &&
        handledCustomers.length > 0 &&
        !currentResult &&
        !showTimeSkipModal &&
        !isPaused &&
        loadedScenarioData &&
        !timeSkipNotificationShownRef.current) {

      // Set flag immediately to prevent multiple calls
      timeSkipNotificationShownRef.current = true;

      // Small delay to ensure all state updates are complete
      const timer = setTimeout(() => {
        checkAndSkipTime();
      }, 300);

      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customers.length, handledCustomers.length, currentResult, isPaused, loadedScenarioData]);

  const handleCustomerClick = (customer) => {
    setSelectedCustomer(customer);

    // Clear all selections - user must manually select options
    setSelectedDecisions({});

    // Track when customer was selected (for communication timing calculation)
    const getScenarioStartTimeInSeconds = () => {
      const startTime = currentScenario.time?.start || '08:00';
      const [hours, minutes] = startTime.split(':').map(Number);
      return hours * 3600 + minutes * 60;
    };
    const scenarioStartSeconds = getScenarioStartTimeInSeconds();
    const timeScale = currentScenario.time?.time_scale || 12;
    const elapsedSeconds = gameTime * timeScale;
    const currentGameTimeSeconds = scenarioStartSeconds + elapsedSeconds;
    const currentGameTime = formatGameTime(currentGameTimeSeconds);

    setCustomerTracking(prev => ({
      ...prev,
      [customer.id]: {
        selected_at: currentGameTime,
        selected_at_game_time: gameTime // Store gameTime for accurate calculation
      }
    }));
  };

  const handleSelectDecision = (category, option) => {
    setSelectedDecisions(prev => ({
      ...prev,
      [category]: option
    }));
  };

  const handleConfirmSolution = () => {
    if (!selectedCustomer) return;

    // Prevent double submission
    if (isProcessingRef.current) {
      console.warn('Already processing customer, ignoring duplicate call');
      return;
    }

    isProcessingRef.current = true;

    // Calculate real response time from customer appearance to confirmation
    const getScenarioStartTimeInSeconds = () => {
      const startTime = currentScenario.time?.start || '08:00';
      const [hours, minutes] = startTime.split(':').map(Number);
      return hours * 3600 + minutes * 60;
    };
    const scenarioStartSeconds = getScenarioStartTimeInSeconds();
    const timeScale = currentScenario.time?.time_scale || 12;
    const elapsedSeconds = gameTime * timeScale;
    const currentGameTimeSeconds = scenarioStartSeconds + elapsedSeconds;

    // Calculate customer appearance time in seconds from scenario start
    const [appearHours, appearMinutes] = (selectedCustomer.appears_at || '08:00').split(':').map(Number);
    const customerDay = selectedCustomer.appears_at_day || 1;
    const customerAppearSeconds = (customerDay - 1) * 24 * 3600 + appearHours * 3600 + appearMinutes * 60;

    // Response time is the difference between current time and customer appearance time
    const responseTime = Math.max(0, Math.floor((currentGameTimeSeconds - customerAppearSeconds)));

    // Get customer tracking data for communication timing calculation
    const tracking = customerTracking[selectedCustomer.id] || null;

    // Prepare scenario data with crisis_start_time for timing calculation
    const scenarioForTiming = {
      ...currentScenario,
      crisis_start_time: currentScenario.crisis?.trigger?.announced_at || currentScenario.time?.start || '08:00'
    };

    const csat = calculateCSAT(selectedDecisions, selectedCustomer, responseTime, tracking, scenarioForTiming);
    const cost = calculateTotalCost(selectedDecisions);

    // Calculate communication timing based on response time in seconds
    // timing: 'proactive' (≤5 min = 300s), 'reactive' (5-15 min = 300-900s), 'delayed' (>15 min = >900s)
    const responseTimeMinutes = responseTime / 60;
    const timing = responseTimeMinutes <= 5 ? 'proactive' : responseTimeMinutes <= 15 ? 'reactive' : 'delayed';

    // Get communication CSAT for breakdown
    const communicationCSAT = selectedDecisions.communication
      ? getCommunicationCSAT(selectedDecisions.communication, selectedCustomer.profile.tier, tracking, scenarioForTiming, selectedCustomer)
      : 0;

    // Calculate CSAT breakdown
    const baseCSAT = 50;
    const transportCSAT = selectedDecisions.transport?.csat || 0;
    const accommodationCSAT = selectedDecisions.accommodation?.csat || 0;
    const compensationCSAT = selectedDecisions.compensation?.csat || 0;
    const subtotal = baseCSAT + transportCSAT + communicationCSAT + accommodationCSAT + compensationCSAT;

    const tierMultipliers = {
      'VIP': 1.5,
      'Business': 1.0,
      'Standard': 0.8
    };
    const tierMultiplier = tierMultipliers[selectedCustomer.profile.tier] || 1.0;

    const slaSeconds = {
      'VIP': 300,
      'Business': 600,
      'Standard': 900
    };
    const timeFactor = responseTime <= slaSeconds[selectedCustomer.profile.tier] ? 1.0 : 0.7;

    // Prepare timing data (using already calculated values)
    const currentGameTime = formatGameTimeWithDay(currentGameTimeSeconds);

    // Calculate crisis start time in seconds
    const [crisisHours, crisisMinutes] = (scenarioForTiming.crisis_start_time || currentScenario.time?.start || '08:00').split(':').map(Number);
    const crisisStartSeconds = crisisHours * 3600 + crisisMinutes * 60;
    const crisisTime = formatGameTimeWithDay(crisisStartSeconds);

    // Contact time - when customer was selected (or current time if not tracked)
    const contactTime = tracking?.selected_at_game_time !== undefined 
      ? formatGameTimeWithDay(scenarioStartSeconds + tracking.selected_at_game_time * timeScale)
      : currentGameTime;

    const confirmTime = currentGameTime;
    const customerAppearsAt = formatGameTimeWithDay(customerAppearSeconds);

    // Calculate delay in minutes (using seconds for accuracy)
    const delayMinutes = Math.floor((currentGameTimeSeconds - customerAppearSeconds) / 60);
    
    // Total time from crisis start
    const totalMinutes = Math.floor((currentGameTimeSeconds - crisisStartSeconds) / 60);

    // Check if solution was optimal (all recommended options selected)
    const wasOptimal =
      selectedDecisions.transport?.recommended &&
      selectedDecisions.communication?.recommended &&
      (!selectedDecisions.accommodation || selectedDecisions.accommodation?.recommended) &&
      (!selectedDecisions.compensation || selectedDecisions.compensation?.recommended);

    // Prepare customer result for debriefing
    // Add default accommodation and compensation if not selected
    const defaultAccommodation = {
      id: 'no_hotel',
      name: 'No Hotel',
      cost: 0,
      csat: 0,
      recommended: false,
    };
    const defaultCompensation = {
      id: 'apology',
      name: 'Apology',
      cost: 0,
      csat: 0,
      recommended: false,
    };

    const customerResult = {
      customer: selectedCustomer,
      choices: {
        transport: selectedDecisions.transport ? {
          id: selectedDecisions.transport.id,
          name: selectedDecisions.transport.name,
          cost: selectedDecisions.transport.cost,
          csat: selectedDecisions.transport.csat,
          recommended: selectedDecisions.transport.recommended || false,
        } : undefined,
        communication: selectedDecisions.communication ? {
          id: selectedDecisions.communication.id,
          name: selectedDecisions.communication.name,
          channel: selectedDecisions.communication.details?.channel,
          tone: selectedDecisions.communication.details?.tone,
          csat: communicationCSAT,
          recommended: selectedDecisions.communication.recommended || false,
        } : undefined,
        accommodation: selectedDecisions.accommodation ? {
          id: selectedDecisions.accommodation.id,
          name: selectedDecisions.accommodation.name,
          cost: selectedDecisions.accommodation.cost,
          csat: selectedDecisions.accommodation.csat,
          recommended: selectedDecisions.accommodation.recommended || false,
        } : defaultAccommodation,
        compensation: selectedDecisions.compensation ? {
          id: selectedDecisions.compensation.id,
          name: selectedDecisions.compensation.name,
          cost: selectedDecisions.compensation.cost,
          csat: selectedDecisions.compensation.csat,
          recommended: selectedDecisions.compensation.recommended || false,
        } : defaultCompensation,
      },
      csat,
      responseTime,
      cost,
      protocolViolations: [], // TODO: Calculate protocol violations
      timing,
      wasOptimal,
      timing_data: {
        crisis_time: crisisTime,
        contact_time: contactTime,
        confirm_time: confirmTime,
        delay_minutes: delayMinutes,
        total_time: totalMinutes,
      },
      csat_breakdown: {
        subtotal,
        tier_multiplier: tierMultiplier,
        time_factor: timeFactor,
      },
      optimal_suggestions: wasOptimal ? [] : (() => {
        const suggestions = [];

        // Transport suggestion
        if (!selectedDecisions.transport?.recommended) {
          const recommendedTransport = selectedCustomer.options?.transport?.find(opt => opt.recommended);
          if (recommendedTransport?.why) {
            suggestions.push(`Transport: ${recommendedTransport.why}`);
          } else {
            suggestions.push('Consider selecting the recommended transport option');
          }
        }

        // Communication suggestion
        if (!selectedDecisions.communication?.recommended) {
          const recommendedCommunication = selectedCustomer.options?.communication?.find(opt => opt.recommended);
          if (recommendedCommunication?.why) {
            suggestions.push(`Communication: ${recommendedCommunication.why}`);
          } else {
            suggestions.push('Consider using the recommended communication channel and tone');
          }
        }

        // Accommodation suggestion
        if (selectedDecisions.accommodation && !selectedDecisions.accommodation?.recommended) {
          const recommendedAccommodation = selectedCustomer.options?.accommodation?.find(opt => opt.recommended);
          if (recommendedAccommodation?.why) {
            suggestions.push(`Accommodation: ${recommendedAccommodation.why}`);
          } else {
            suggestions.push('Consider the recommended accommodation option');
          }
        }

        // Compensation suggestion
        if (selectedDecisions.compensation && !selectedDecisions.compensation?.recommended) {
          const recommendedCompensation = selectedCustomer.options?.compensation?.find(opt => opt.recommended);
          if (recommendedCompensation?.why) {
            suggestions.push(`Compensation: ${recommendedCompensation.why}`);
          } else {
            suggestions.push('Consider the recommended compensation option');
          }
        }

        return suggestions;
      })(),
    };

    // Update stats
    setStats(prev => ({
      totalCsat: prev.totalCsat + csat,
      customersHandled: prev.customersHandled + 1,
      budgetSpent: prev.budgetSpent + cost,
      avgResponseTime: (prev.avgResponseTime * prev.customersHandled + responseTime) / (prev.customersHandled + 1)
    }));

    setBudget(prev => Math.max(0, prev - cost));

    // Save customer result for debriefing (prevent duplicates)
    setCustomerResults(prev => {
      // Check if this customer already exists in results
      const exists = prev.some(r => r.customer.id === selectedCustomer.id);
      if (exists) {
        console.warn(`Customer ${selectedCustomer.id} already in results, skipping duplicate`);
        return prev;
      }
      return [...prev, customerResult];
    });

    // Show result
    setCurrentResult({
      customer: selectedCustomer,
      decisions: selectedDecisions,
      csat,
      cost,
      responseTime
    });

    // Move customer to handled
    setHandledCustomers(prev => [...prev, selectedCustomer]);
    setCustomers(prev => prev.filter(c => c.id !== selectedCustomer.id));
    setSelectedCustomer(null);

    // Reset processing flag after a short delay
    setTimeout(() => {
      isProcessingRef.current = false;
    }, 500);
  };

  const handleCloseResult = () => {
    setCurrentResult(null);
    // Clear selected customer and decisions to allow selecting next customer
    setSelectedCustomer(null);
    setSelectedDecisions({});
    // Time skip will be triggered automatically by useEffect
  };

  // Check if scenario is complete and show debriefing
  useEffect(() => {
    if (!loadedScenarioData || !currentScenario) return;

    const initialCustomerCount = loadedScenarioData.customers?.length || 0;

    // Check if all customers have been handled
    if (initialCustomerCount > 0 &&
        customers.length === 0 &&
        handledCustomers.length >= initialCustomerCount &&
        customerResults.length >= initialCustomerCount &&
        !showDebriefing) {

      // Prepare debriefing data
      // Calculate average CSAT directly from customerResults to ensure accuracy
      const averageCSAT = customerResults.length > 0
        ? customerResults.reduce((sum, r) => sum + (r.csat || 0), 0) / customerResults.length
        : 0;

      // Calculate protocol violations based on prioritization and timing
      // Check if VIP customers were served slower than non-VIP
      const vipResults = customerResults.filter((r) => r.customer.profile.tier === 'VIP');
      const nonVipResults = customerResults.filter((r) => r.customer.profile.tier !== 'VIP');

      const avgVIPResponse = vipResults.length > 0
        ? vipResults.reduce((sum, r) => sum + r.responseTime, 0) / vipResults.length
        : 0;
      const avgNonVIPResponse = nonVipResults.length > 0
        ? nonVipResults.reduce((sum, r) => sum + r.responseTime, 0) / nonVipResults.length
        : 0;

      // Count violations: VIP served slower than average or slower than non-VIP
      let prioritizationViolations = 0;
      if (vipResults.length > 0 && avgVIPResponse > 0) {
        const avgResponse = stats.averageResponseTime || 0;
        if (avgVIPResponse > avgResponse * 1.2 || (nonVipResults.length > 0 && avgVIPResponse > avgNonVIPResponse)) {
          prioritizationViolations = vipResults.length; // One violation per VIP served slowly
        }
      }

      // Count delayed communication violations
      const delayedCount = customerResults.filter((r) => r.timing === 'delayed').length;

      const totalViolations = prioritizationViolations + delayedCount;

      const finalStats = {
        totalCustomers: initialCustomerCount,
        customersServed: handledCustomers.length,
        averageCSAT: averageCSAT,
        totalCost: stats.budgetSpent,
        budgetRemaining: budget,
        averageResponseTime: stats.avgResponseTime,
        totalViolations: totalViolations,
      };

      const finalScore = calculateFinalScore(
        customerResults,
        finalStats,
        budget + stats.budgetSpent, // Original budget
        currentScenario
      );

      const benchmarks = {
        expertScore: 92,
        averageScore: 65,
      };

      const recommendations = generateRecommendations(customerResults, finalStats, finalScore);
      const skillGaps = generateSkillGaps(customerResults, finalStats, finalScore);
      const timelineEvents = generateTimelineEvents(customerResults, currentScenario);

      const debriefingData = {
        scenario: {
          id: currentScenario.id,
          name: currentScenario.name,
          difficulty: currentScenario.difficulty || 1,
        },
        finalScore,
        customerResults,
        stats: finalStats,
        benchmarks,
        recommendations,
        skillGaps,
        timelineEvents,
      };

      setDebriefingData(debriefingData);
      setShowDebriefing(true);
    }
  }, [customers.length, handledCustomers.length, customerResults, loadedScenarioData, currentScenario, stats, budget, showDebriefing]);

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const cycleSpeed = () => {
    setGameSpeed(prev => prev === 1 ? 2 : prev === 2 ? 3 : 1);
  };

  // Calculate current game time starting from scenario start time
  const getScenarioStartTimeInSeconds = () => {
    const startTime = currentScenario.time?.start || '08:00';
    const [hours, minutes] = startTime.split(':').map(Number);
    return (hours || 8) * 3600 + (minutes || 0) * 60;
  };

  const scenarioStartSeconds = getScenarioStartTimeInSeconds();
  const elapsedSeconds = gameTime * (currentScenario.time.time_scale || 12);
  const currentGameTimeSeconds = scenarioStartSeconds + elapsedSeconds;

  // Ensure currentGameTimeSeconds is valid
  const validGameTimeSeconds = isNaN(currentGameTimeSeconds) || currentGameTimeSeconds < 0
    ? scenarioStartSeconds
    : currentGameTimeSeconds;

  const currentGameTime = formatGameTimeWithDay(validGameTimeSeconds);

  // Calculate day (always starts from day 1)
  const totalDays = Math.floor(validGameTimeSeconds / 86400);
  const currentDay = Math.max(1, totalDays + 1); // Always at least day 1

  // Calculate current time in minutes for headline matching
  const currentTimeInDay = Math.floor((validGameTimeSeconds % 86400) / 60);
  const [currentHours, currentMinutes] = Math.floor(validGameTimeSeconds / 60) % (24 * 60) >= 0 
    ? [Math.floor(currentTimeInDay / 60), currentTimeInDay % 60]
    : [0, 0];
  const currentTimeString = `${String(currentHours).padStart(2, '0')}:${String(currentMinutes).padStart(2, '0')}`;

  // Find current headlines based on time_events
  const timeEvents = currentScenario.time_events || [];
  let currentHeadlines = [];
  
  // Find the most recent event that should be active
  for (let i = timeEvents.length - 1; i >= 0; i--) {
    const event = timeEvents[i];
    const [eventHours, eventMinutes] = event.time.split(':').map(Number);
    const eventTimeMinutes = eventHours * 60 + eventMinutes;
    
    if (event.day <= currentDay) {
      // If same day, check if time has passed
      if (event.day < currentDay || (event.day === currentDay && eventTimeMinutes <= currentTimeInDay)) {
        // Get all headlines from this event
        currentHeadlines = event.headlines || [];
        break;
      }
    }
  }
  
  // Fallback to crisis description if no headlines found
  if (currentHeadlines.length === 0) {
    currentHeadlines = [currentScenario.crisis?.description || ''];
  }

  const avgCsat = stats.customersHandled > 0 ? Math.round(stats.totalCsat / stats.customersHandled) : 0;

  // Show debriefing if scenario is complete
  if (showDebriefing && debriefingData) {
    return (
      <Debriefing
        {...debriefingData}
        onExit={() => {
          setShowDebriefing(false);
          onExit?.();
        }}
      />
    );
  }

  return (
    <div className="h-screen w-screen bg-background overflow-hidden flex flex-col">
      {/* Top Bar */}
      <header className="h-16 bg-card border-b border-border flex items-center px-6 gap-6 relative z-10">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-[hsl(var(--info))]" />
            <div>
              <p className="font-mono font-bold">{currentGameTime} CEST</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={togglePause}
              className="w-20"
            >
              {isPaused ? <Play className="w-4 h-4 mr-1" /> : <Pause className="w-4 h-4 mr-1" />}
              {isPaused ? 'Play' : 'Pause'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={cycleSpeed}
              className="w-16"
            >
              <FastForward className="w-4 h-4 mr-1" />
              {gameSpeed}x
            </Button>
          </div>
        </div>

        {/* News Ticker */}
        <div className="flex-1 overflow-hidden relative min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {currentHeadlines.map((headline, index) => (
              <AlertBanner
                key={index}
                type="critical"
                message={headline}
              />
            ))}
            <AlertBanner
              type="info"
              message={`${customers.length} travelers awaiting assistance`}
            />
          </div>
        </div>

        {/* Stats Quick View */}
        <div className="flex items-center gap-4">
          <StatBadge label="CSAT" value={`${avgCsat}%`} />
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowDebug(!showDebug)}
          >
            <Settings className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Customer Queue */}
        <aside className="w-80 bg-card border-r border-border flex flex-col">
          <div className="p-4 border-b border-border flex-shrink-0">
            <h2 className="text-lg font-semibold mb-1">Customer Queue</h2>
            <p className="text-sm text-muted-foreground">
              {customers.length} travelers waiting
            </p>
          </div>

          <ScrollArea className="flex-1 scrollbar-thin">
            <div className="p-2 space-y-2">
              <AnimatePresence>
                {customers.map(customer => (
                  <CustomerCard
                    key={customer.id}
                    customer={customer}
                    currentTime={currentGameTime}
                    currentDay={currentDay}
                    onClick={() => handleCustomerClick(customer)}
                    isSelected={selectedCustomer?.id === customer.id}
                  />
                ))}
              </AnimatePresence>

              {customers.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>All customers handled!</p>
                </div>
              )}
            </div>
          </ScrollArea>
        </aside>

        {/* Center - Map/Workspace */}
        <main className="flex-1 relative bg-[hsl(var(--background))]">
          <EuropeMap
            scenario={scenario}
            activeRoutes={activeRoutes}
            weatherEvents={[
              // Add weather events based on scenario
              ...(scenario.crisis.type === 'weather' ? [
                { type: 'storm', location: 'AMS' },
                { type: 'storm', location: 'FRA' }
              ] : [])
            ]}
          />
        </main>

        {/* Right Sidebar - Metrics Dashboard */}
        <aside className="w-96 bg-card border-l border-border">
          <div className="p-6 space-y-6">
            {/* Performance Score */}
            <Card>
              <CardContent className="p-6">
                <div className="text-center mb-4">
                  <p className="text-sm text-muted-foreground mb-2">Average CSAT</p>
                  <motion.div
                    key={avgCsat}
                    initial={{ scale: 0.8 }}
                    animate={{ scale: 1 }}
                    className="relative w-32 h-32 mx-auto"
                  >
                    <svg className="w-full h-full transform -rotate-90">
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="hsl(var(--muted))"
                        strokeWidth="8"
                        fill="none"
                      />
                      <circle
                        cx="64"
                        cy="64"
                        r="56"
                        stroke="hsl(var(--primary))"
                        strokeWidth="8"
                        fill="none"
                        strokeDasharray={`${(avgCsat / 100) * 352} 352`}
                        className="transition-all duration-500"
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-3xl font-bold">{avgCsat}%</span>
                    </div>
                  </motion.div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div>
                    <p className="text-xs text-muted-foreground">Handled</p>
                    <p className="text-lg font-bold">{stats.customersHandled}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Remaining</p>
                    <p className="text-lg font-bold">{customers.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Budget Tracker */}
            <Card>
              <CardContent className="p-4">
                <p className="text-sm font-medium mb-2">Crisis Budget</p>
                <p className="text-2xl font-bold font-mono mb-2">
                  {formatCurrency(budget || 0)}
                  {currentScenario.budget && (
                    <span className="text-sm text-muted-foreground"> / {formatCurrency(currentScenario.budget)}</span>
                  )}
                </p>
                {currentScenario.budget && (
                  <Progress
                    value={((budget || 0) / currentScenario.budget) * 100}
                    className="h-2"
                  />
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  {formatCurrency(stats.budgetSpent)} spent
                </p>
              </CardContent>
            </Card>

            {/* Response Time */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium">Avg. Response Time</p>
                    <p className="text-2xl font-bold font-mono">
                      {(stats.avgResponseTime / 60).toFixed(1)}m
                    </p>
                  </div>
                  <Clock className="w-8 h-8 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>

            <Button
              variant="outline"
              className="w-full"
              onClick={onExit}
            >
              Exit Scenario
            </Button>
          </div>
        </aside>
      </div>

      {/* Decision Panel */}
      <AnimatePresence>
        {selectedCustomer && (
          <DecisionPanel
            customer={selectedCustomer}
            transportOptions={selectedCustomer.options?.transport || []}
            communicationOptions={selectedCustomer.options?.communication || []}
            accommodationOptions={selectedCustomer.options?.accommodation || []}
            compensationOptions={selectedCustomer.options?.compensation || []}
            selectedDecisions={selectedDecisions}
            onSelectDecision={handleSelectDecision}
            onConfirm={handleConfirmSolution}
            onCancel={() => setSelectedCustomer(null)}
            budget={budget}
          />
        )}
      </AnimatePresence>

      {/* Result Modal */}
      <AnimatePresence>
        {currentResult && (
          <ResultModal
            {...currentResult}
            onClose={handleCloseResult}
          />
        )}
      </AnimatePresence>

      {/* Debug Panel */}
      {showDebug && (
        <DebugPanel
          gameState={{
            gameTime,
            customers,
            handledCustomers,
            stats,
            budget
          }}
          onClose={() => setShowDebug(false)}
        />
      )}

      {/* Time Skip Modal */}
      <TimeSkipModal
        open={showTimeSkipModal}
        timeSkipInfo={timeSkipInfo}
        onClose={() => {
          setTimeSkipInfo(null);
          setShowTimeSkipModal(false);
          timeSkipNotificationShownRef.current = false;
          setIsTimeSkipping(false);
        }}
      />
    </div>
  );
};

// Time Skip Modal Component with Enter key support
const TimeSkipModal = ({ open, timeSkipInfo, onClose }) => {
  // Handle Enter key to close modal
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && !event.shiftKey && !event.ctrlKey && !event.metaKey) {
        // Don't trigger if user is typing in an input field
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
          return;
        }

        event.preventDefault();
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onClose]);

  return (
    <Dialog open={open} onOpenChange={() => {
      // Prevent closing by clicking outside - user must click OK
    }}>
      <DialogContent
        className="sm:max-w-[500px] [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            {timeSkipInfo?.timeSkipped ? (
              <>
                <FastForward className="w-6 h-6 text-[hsl(var(--info))]" />
                Time Accelerated
              </>
            ) : (
              <>
                <Clock className="w-6 h-6 text-[hsl(var(--info))]" />
                Scenario Start
              </>
            )}
          </DialogTitle>
        </DialogHeader>
        {timeSkipInfo && (
          <div className="space-y-4 pt-2">
            {timeSkipInfo.eventDescription ? (
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                <p className="text-base text-gray-200 leading-relaxed whitespace-pre-line">
                  {timeSkipInfo.eventDescription}
                </p>
              </div>
            ) : (
              <p className="text-lg text-gray-50">
                Time has been accelerated. {timeSkipInfo.newCustomersCount > 0 ? 'New customers have been added to the queue.' : 'Waiting for next event...'}
              </p>
            )}
            <div className="bg-slate-800 rounded-lg p-4 space-y-2">
              {timeSkipInfo.timeSkipped && (
                <div className="flex items-center justify-between">
                  <span className="text-gray-400">Time skipped:</span>
                  <span className="text-xl font-semibold text-gray-50">{timeSkipInfo.timeText}</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="text-gray-400">Current Time:</span>
                <span className="text-xl font-mono font-semibold text-[hsl(var(--info))]">
                  {timeSkipInfo.currentTime}
                </span>
              </div>
              {timeSkipInfo.newCustomersCount > 0 && (
                <div className="flex items-center justify-between pt-2 border-t border-white/10">
                  <span className="text-gray-400">{timeSkipInfo.timeSkipped ? 'New customers:' : 'Initial customers:'}</span>
                  <span className="text-xl font-semibold text-emerald-400">
                    {timeSkipInfo.newCustomersCount} {timeSkipInfo.newCustomersCount === 1 ? 'customer' : 'customers'} {timeSkipInfo.timeSkipped ? 'added' : 'waiting'}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
        <div className="flex justify-end pt-4">
          <Button
            onClick={onClose}
            className="min-w-[100px]"
          >
            OK
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AlertBanner = ({ type, message }) => {
  const typeColors = {
    critical: 'bg-[hsl(var(--critical))]/20 text-[hsl(var(--critical))] border-[hsl(var(--critical))]/50',
    warning: 'bg-[hsl(var(--high))]/20 text-[hsl(var(--high))] border-[hsl(var(--high))]/50',
    info: 'bg-[hsl(var(--info))]/20 text-[hsl(var(--info))] border-[hsl(var(--info))]/50'
  };

  return (
    <Badge variant="outline" className={`px-3 py-1 ${typeColors[type]} flex-shrink-0`}>
      <AlertTriangle className="w-3 h-3 mr-1 flex-shrink-0" />
      <span>{message}</span>
    </Badge>
  );
};

const StatBadge = ({ label, value, icon }) => {
  return (
    <div className="text-center">
      <p className="text-xs text-muted-foreground">{label}</p>
      <div className="flex items-center gap-1">
        {icon && <span className="text-[hsl(var(--primary))]">{icon}</span>}
        <p className="font-mono font-bold">{value}</p>
      </div>
    </div>
  );
};